import React from 'react';
import { FieldDefinition, FormState } from './types';

export type FieldRenderer<T = FormState> = (
  field: FieldDefinition,
  formState: T,
  updateFormState: (updater: (prev: T) => T) => void,
  context?: FieldRendererContext
) => React.ReactElement | null;

export interface FieldRendererContext {
  initialAudioId?: string | number;
}

export type FieldValueExtractor = (
  field: FieldDefinition,
  cardItem: any
) => any;

export type UpdateHandler = (
  field: FieldDefinition,
  currentValue: any,
  cardItem: any,
  cardId: string,
  context: {
    updateFile: (cardId: string, media: any, updateType: string) => void;
    addToBatch: (fieldName: string, value: any) => void;
  }
) => void;

export type GlobalPlaceholderResolver = (
  field: FieldDefinition,
  globalPlaceholders: any
) => any;

export type CreateSerializer = (
  field: FieldDefinition,
  currentValue: any,
  data: Record<string, any>
) => void;

export type ApiSerializer = CreateSerializer;

export type StoreSerializer = (
  field: FieldDefinition,
  currentValue: any,
  newItem: Record<string, any>
) => void;

export type ContextProvider = (
  field: FieldDefinition,
  cardItem: any
) => FieldRendererContext;

export type ValidationHandler = (
  field: FieldDefinition,
  value: any
) => { valid: boolean; error?: string; fieldName?: string } | null;

export interface FieldRegistration {
  renderer: FieldRenderer;
  extractor?: FieldValueExtractor;
  updateHandler?: UpdateHandler;
  placeholderResolver?: GlobalPlaceholderResolver;
  createSerializer?: CreateSerializer;
  apiSerializer?: ApiSerializer;
  storeSerializer?: StoreSerializer;
  contextProvider?: ContextProvider;
  validator?: ValidationHandler;
}

class FieldRendererRegistry {
  private registrations = new Map<string, FieldRegistration>();

  register(
    fieldType: string,
    optionsOrRenderer: FieldRegistration | FieldRenderer
  ): void {
    if (this.registrations.has(fieldType)) {
      console.warn(
        `Overwriting existing renderer for field type: ${fieldType}`
      );
    }

    if (typeof optionsOrRenderer === 'function') {
      this.registrations.set(fieldType, { renderer: optionsOrRenderer });
    } else {
      this.registrations.set(fieldType, optionsOrRenderer);
    }
  }

  get(fieldType: string): FieldRenderer | undefined {
    return this.registrations.get(fieldType)?.renderer;
  }

  render(
    field: FieldDefinition,
    formState: FormState,
    updateFormState: (updater: (prev: FormState) => FormState) => void,
    context?: FieldRendererContext
  ): React.ReactElement | null {
    const renderer = this.get(field.type);
    if (!renderer) {
      console.error(`No renderer registered for field type: ${field.type}`);
      return null;
    }

    return renderer(field, formState, updateFormState, context);
  }

  hasRenderer(fieldType: string): boolean {
    return this.registrations.has(fieldType);
  }

  getAllFieldTypes(): string[] {
    return Array.from(this.registrations.keys());
  }

  extractValue(field: FieldDefinition, cardItem: any): any {
    const registration = this.registrations.get(field.type);
    const extractor = registration?.extractor;
    if (!extractor) {
      return cardItem?.[field.name] || null;
    }
    return extractor(field, cardItem);
  }

  getUpdateHandler(fieldType: string): UpdateHandler | undefined {
    return this.registrations.get(fieldType)?.updateHandler;
  }

  getPlaceholderResolver(
    fieldType: string
  ): GlobalPlaceholderResolver | undefined {
    return this.registrations.get(fieldType)?.placeholderResolver;
  }

  getCreateSerializer(fieldType: string): CreateSerializer | undefined {
    return this.registrations.get(fieldType)?.createSerializer;
  }

  getApiSerializer(fieldType: string): ApiSerializer | undefined {
    const registration = this.registrations.get(fieldType);
    return registration?.apiSerializer || registration?.createSerializer;
  }

  getStoreSerializer(fieldType: string): StoreSerializer | undefined {
    return this.registrations.get(fieldType)?.storeSerializer;
  }

  getContextProvider(fieldType: string): ContextProvider | undefined {
    return this.registrations.get(fieldType)?.contextProvider;
  }

  getValidator(fieldType: string): ValidationHandler | undefined {
    return this.registrations.get(fieldType)?.validator;
  }
}

export const fieldRegistry = new FieldRendererRegistry();
